import type { MySuspension } from '../suspensions/useMySuspensions'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { apiClient } from '../../apiClient'

export type MyTrainingReset = {
  trainingResetId: string
  resetAt: string
  liveProgramId: string
  trainingProgramId: string
  program: MySuspension['program']
}

export const MSMyTrainingResetsKey = 'mission-specialist-my-training-resets'

const request = async (): Promise<MyTrainingReset[]> => {
  const { data } = await apiClient.get<MyTrainingReset[]>('/int/v3/training-resets/mine')
  return data
}

export function useMyTrainingResets() {
  return useQuery<MyTrainingReset[]>({
    queryKey: [MSMyTrainingResetsKey],
    queryFn: request,
    refetchOnWindowFocus: true,
  })
}

export function useInvalidateMyTrainingResets() {
  const queryClient = useQueryClient()
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: [MSMyTrainingResetsKey] }),
    [queryClient]
  )
}

export function useCloseTrainingResets() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (trainingResetIds: string[]) => {
      await Promise.all(trainingResetIds.map((id) => apiClient.put(`/int/v3/training-resets/${id}/close`)))
    },
    onMutate: async (trainingResetIds) => {
      await queryClient.cancelQueries({ queryKey: [MSMyTrainingResetsKey] })
      const previous = queryClient.getQueryData<MyTrainingReset[]>([MSMyTrainingResetsKey])
      queryClient.setQueryData<MyTrainingReset[]>([MSMyTrainingResetsKey], (old) =>
        old?.filter((reset) => !trainingResetIds.includes(reset.trainingResetId))
      )
      return { previous }
    },
    onError: (_error, _ids, context) => {
      queryClient.setQueryData([MSMyTrainingResetsKey], context?.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: [MSMyTrainingResetsKey] }),
  })
}
